import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Terminal, 
  CheckCircle2, 
  AlertCircle, 
  BookOpen, 
  Play, 
  Code, 
  Briefcase, 
  ChevronRight,
  ShieldCheck,
  Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TopicItem } from '@/data/platformTopics';
import { toast } from 'sonner';

interface ModulePracticeModalProps {
  topic: TopicItem | null;
  onClose: () => void;
}

interface PracticeTask {
  id: string;
  type: 'quiz' | 'case' | 'code';
  title: string;
  description: string;
  options?: string[];
  correctOption?: number;
  starterCode?: string;
  rubricHint?: string;
}

const TOPIC_TASKS: Record<string, PracticeTask[]> = {
  fintech_api: [
    {
      id: 'fin_case_1',
      type: 'case',
      title: 'Кейс: Верификация цифровой подписи HMAC-SHA256',
      description: 'Финтех-сервис получает Webhook от платежного шлюза с заголовком X-Signature-SHA256. В теле запроса сумма, ID транзакции и timestamp. Опишите последовательность действий сервера для валидации запроса и защиты от Replay Attack.',
      rubricHint: 'Укажите проверку timestamp на окно допустимости (например 5 минут), генерацию HMAC от сырого body с секретом и безопасное сравнение (timing-safe).'
    },
    {
      id: 'fin_quiz_1',
      type: 'quiz',
      title: 'Тест: Стандарты безопасности Open Banking API',
      description: 'Какой механизм авторизации является обязательным стандартом для межбанковского обмена согласно протоколу FAPI (Financial-grade API)?',
      options: [
        'Базовая HTTP аутентификация по логину и паролю',
        'OAuth 2.0 с mTLS (Mutual TLS) или JWT/DPoP токенами',
        'Сессионные Cookie с атрибутом SameSite=Strict',
        'Статический API-ключ в query-параметрах'
      ],
      correctOption: 1
    }
  ],
  uzdigcomp_sec: [
    {
      id: 'sec_case_1',
      type: 'case',
      title: 'Кейс: Защита от утечки в корпоративной LLM (Data Leak Prevention)',
      description: 'Сотрудники банка загружают неструктурированные финансовые отчеты и выписки в систему с корпоративным ИИ. Какие архитектурные слои фильтрации необходимо внедрить до отправки запроса в стороннюю LLM?',
      rubricHint: 'Упомяните PII-маскирование (деперсонализацию), локальный токенайзер, фильтр секретов и аудит-лог.'
    },
    {
      id: 'sec_quiz_1',
      type: 'quiz',
      title: 'Тест: Оценка надежности парольной политики (UzDigComp-AI)',
      description: 'Согласно стандарту NIST SP 800-63B и доменным требованиям UzDigComp-AI, какая рекомендация является наиболее эффективной против компрометации учетных записей?',
      options: [
        'Принудительная смена сложного пароля каждые 30 дней',
        'Многофакторная аутентификация (MFA/2FA) и сверка с базой скомпрометированных паролей',
        'Требование обязательного спецсимвола и запрет повторения 1 буквы',
        'Ограничение длины пароля до 8 символов'
      ],
      correctOption: 1
    }
  ]
};

const DEFAULT_TASKS: PracticeTask[] = [
  {
    id: 'def_case',
    type: 'case',
    title: 'Аналитический кейс модуля',
    description: 'Опишите реальный практический сценарий применения концепций данного модуля в банковских или enterprise-системах. С какими узкими местами (bottlenecks) может столкнуться инженер?',
    rubricHint: 'Развернутый ответ с анализом архитектуры и алгоритмической сложности.'
  },
  {
    id: 'def_quiz',
    type: 'quiz',
    title: 'Проверка базовой концепции',
    description: 'Что является ключевым критерием качества при проектировании масштабируемых сервисов в рамках данной темы?',
    options: [
      'Минимизация времени выполнения и декомпозиция ответственности (SOLID)',
      'Написание всего функционала в одном монолитном файле для ускорения чтения',
      'Отказ от логирования и обработки исключений',
      'Использование глобальных переменных для экономии памяти'
    ],
    correctOption: 0
  }
];

export default function ModulePracticeModal({ topic, onClose }: ModulePracticeModalProps) {
  const navigate = useNavigate();
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [caseText, setCaseText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!topic) return null;

  const tasks = TOPIC_TASKS[topic.id] || DEFAULT_TASKS;
  const currentTask = tasks[activeTaskIndex] || tasks[0];

  const handleQuizSubmit = () => {
    if (selectedOption === null) {
      toast.error('Выберите один из вариантов ответа');
      return;
    }
    setSubmitted(true);
    if (selectedOption === currentTask.correctOption) {
      toast.success('Верно! Теоретический концепт усвоен.');
    } else {
      toast.error('Неверно. Проанализируйте правильный ответ.');
    }
  };

  const handleCaseSubmit = () => {
    if (caseText.trim().length < 30) {
      toast.error('Опишите решение подробнее (минимум 30 символов)');
      return;
    }
    setSubmitted(true);
    toast.success('Ответ зафиксирован в профиле компетенций!');
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="relative w-full max-w-2xl bg-[#0f0720] border border-white/15 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 text-white overflow-hidden"
        >
          {/* Top Bar */}
          <div className="flex items-start justify-between border-b border-white/10 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-white">
                  {topic.category} · {topic.level}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                  Задание {activeTaskIndex + 1} из {tasks.length}
                </span>
              </div>
              <h3 className="text-xl font-extrabold text-white">
                {topic.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Task Type Tabs */}
          <div className="flex gap-2">
            {tasks.map((t, idx) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setActiveTaskIndex(idx);
                  setSelectedOption(null);
                  setSubmitted(false);
                }}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  activeTaskIndex === idx
                    ? 'glass-btn-primary'
                    : 'glass-btn-secondary text-white/70'
                }`}
              >
                {t.type === 'case' ? 'Кейс-задание' : t.type === 'quiz' ? 'Тест-контроль' : 'Кодинг'}
              </button>
            ))}
          </div>

          {/* Task Content */}
          <div className="glass-panel p-5 rounded-2xl space-y-4">
            <div>
              <h4 className="text-sm font-bold text-white mb-1.5 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-400" />
                {currentTask.title}
              </h4>
              <p className="text-xs text-white/75 leading-relaxed">
                {currentTask.description}
              </p>
            </div>

            {/* Quiz Mode */}
            {currentTask.type === 'quiz' && currentTask.options && (
              <div className="space-y-2 pt-2">
                {currentTask.options.map((opt, i) => {
                  const isSelected = selectedOption === i;
                  const isCorrect = currentTask.correctOption === i;
                  let styleClass = 'border-white/10 bg-white/[0.03] text-white/80 hover:bg-white/10';

                  if (submitted) {
                    if (isCorrect) {
                      styleClass = 'border-emerald-500 bg-emerald-500/20 text-emerald-300 font-semibold';
                    } else if (isSelected) {
                      styleClass = 'border-rose-500 bg-rose-500/20 text-rose-300';
                    }
                  } else if (isSelected) {
                    styleClass = 'border-white bg-white/20 text-white font-bold';
                  }

                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={submitted}
                      onClick={() => setSelectedOption(i)}
                      className={`w-full text-left p-3 rounded-xl border text-xs transition-all flex items-center justify-between ${styleClass}`}
                    >
                      <span>{opt}</span>
                      {submitted && isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 ml-2" />}
                      {submitted && isSelected && !isCorrect && <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 ml-2" />}
                    </button>
                  );
                })}

                {!submitted ? (
                  <button
                    type="button"
                    onClick={handleQuizSubmit}
                    className="w-full mt-3 glass-btn-primary py-2.5 rounded-xl text-xs uppercase tracking-wider"
                  >
                    Подтвердить ответ
                  </button>
                ) : (
                  <div className="pt-2 flex justify-between items-center text-xs">
                    <span className="text-white/60">Ответ зачтен</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTaskIndex < tasks.length - 1) {
                          setActiveTaskIndex(activeTaskIndex + 1);
                          setSelectedOption(null);
                          setSubmitted(false);
                        } else {
                          onClose();
                        }
                      }}
                      className="glass-btn-secondary px-3 py-1.5 rounded-lg text-xs"
                    >
                      {activeTaskIndex < tasks.length - 1 ? 'Следующее задание →' : 'Завершить'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Case Mode */}
            {currentTask.type === 'case' && (
              <div className="space-y-3 pt-2">
                <textarea
                  value={caseText}
                  onChange={(e) => setCaseText(e.target.value)}
                  disabled={submitted}
                  rows={4}
                  placeholder="Опишите ваше архитектурное или прикладное решение кейса..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-white/40 transition-colors"
                />

                {currentTask.rubricHint && (
                  <div className="text-[11px] text-white/50 p-2.5 rounded-lg bg-white/[0.03] border border-white/5">
                    <span className="font-bold text-white/70">Критерий оценки: </span>
                    {currentTask.rubricHint}
                  </div>
                )}

                {!submitted ? (
                  <button
                    type="button"
                    onClick={handleCaseSubmit}
                    className="w-full glass-btn-primary py-2.5 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>Отправить на рецензию</span>
                  </button>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-between">
                    <span>Решение сохранено для валидации</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTaskIndex < tasks.length - 1) {
                          setActiveTaskIndex(activeTaskIndex + 1);
                          setCaseText('');
                          setSubmitted(false);
                        } else {
                          onClose();
                        }
                      }}
                      className="glass-btn-secondary px-3 py-1.5 rounded-lg text-xs"
                    >
                      Далее →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Action to open in IDE optionally */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-white/10">
            <span className="text-[11px] text-white/50">
              Нужна полная среда кодинга?
            </span>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/ide');
                }}
                className="w-full sm:w-auto glass-btn-secondary px-4 py-2 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <Terminal className="w-3.5 h-3.5" />
                <span>Открыть в IDE</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
